import { Scale } from 'lucide-react';
import { Link } from 'react-router-dom';
import MarketingNav from '../components/MarketingNav';
import MarketingFooter from '../components/MarketingFooter';
import '../styles/marketing.css';
import '../styles/legal.css';

const EFFECTIVE_DATE = 'March 15, 2026';

const sections = [
  { id: 'agreement', title: 'Agreement to Terms' },
  { id: 'license', title: 'License Grant' },
  { id: 'restrictions', title: 'Restrictions' },
  { id: 'subscription', title: 'Subscription & Fees' },
  { id: 'data-ownership', title: 'Data Ownership' },
  { id: 'confidentiality', title: 'Confidentiality & HIPAA' },
  { id: 'support', title: 'Support & Maintenance' },
  { id: 'warranty', title: 'Warranty Disclaimers' },
  { id: 'liability', title: 'Limitation of Liability' },
  { id: 'termination', title: 'Termination' },
  { id: 'updates', title: 'Updates & Modifications' },
  { id: 'governing-law', title: 'Governing Law' },
  { id: 'general', title: 'General Provisions' },
  { id: 'contact', title: 'Contact Information' },
];

const EULA = () => {
  return (
    <div className="legal-page">
      <MarketingNav />

      <section className="legal-hero">
        <div className="legal-hero-badge">
          <Scale size={14} />
          Effective {EFFECTIVE_DATE}
        </div>
        <h1>End User License Agreement</h1>
        <p className="legal-hero-subtitle">
          The license agreement governing your use of the Amplify by CareTracker
          software platform.
        </p>
      </section>

      <div className="legal-layout">
        <nav className="legal-toc">
          <h3>Contents</h3>
          <ol>
            {sections.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`}>{s.title}</a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="legal-content">
          {/* 1. Agreement */}
          <section id="agreement" className="legal-section">
            <h2>1. Agreement to Terms</h2>
            <p>
              This End User License Agreement (&quot;EULA&quot; or
              &quot;Agreement&quot;) is a legal agreement between you
              (&quot;User,&quot; &quot;you,&quot; or &quot;your&quot;) and
              Harris CareTracker, a division of Harris Computer Systems
              (&quot;Licensor,&quot; &quot;we,&quot; &quot;us,&quot; or
              &quot;our&quot;), for the use of the Amplify by CareTracker
              software platform and all associated services, documentation, and
              updates (collectively, the &quot;Software&quot;).
            </p>
            <p>
              By installing, accessing, or using the Software, you agree to be
              bound by this EULA. If you are accepting this EULA on behalf of a
              healthcare organization, you represent and warrant that you have
              the authority to bind that organization to the terms of this
              Agreement.
            </p>
            <p>
              This EULA is supplemented by our{' '}
              <Link to="/terms">Terms of Service</Link> and{' '}
              <Link to="/privacy">Privacy Policy</Link>, which are incorporated
              by reference.
            </p>
          </section>

          {/* 2. License Grant */}
          <section id="license" className="legal-section">
            <h2>2. License Grant</h2>

            <h3>2.1 Grant of License</h3>
            <p>
              Subject to the terms of this EULA and your active subscription, we
              grant you a limited, non-exclusive, non-transferable,
              non-sublicensable, revocable license to access and use the
              Software solely for the purpose of clinical documentation within
              your healthcare organization.
            </p>

            <h3>2.2 Scope of License</h3>
            <p>This license permits you to:</p>
            <ul>
              <li>
                Access the Software via web browser on any device
              </li>
              <li>
                Install and use the mobile application on devices owned or
                controlled by you
              </li>
              <li>
                Use the Software&apos;s features in accordance with the
                permissions assigned to your user role
              </li>
              <li>
                Access documentation and training materials provided with the
                Software
              </li>
            </ul>

            <h3>2.3 SaaS Delivery</h3>
            <p>
              The Software is delivered as a Software-as-a-Service (SaaS)
              solution. You access the Software remotely; no local installation
              of server components is required. The mobile applications serve as
              client interfaces to the cloud-hosted service.
            </p>
          </section>

          {/* 3. Restrictions */}
          <section id="restrictions" className="legal-section">
            <h2>3. Restrictions</h2>
            <p>You shall not, directly or indirectly:</p>
            <ul>
              <li>
                Copy, modify, adapt, translate, or create derivative works based
                on the Software
              </li>
              <li>
                Reverse engineer, disassemble, decompile, or attempt to derive
                the source code of the Software
              </li>
              <li>
                Rent, lease, lend, sell, sublicense, distribute, or otherwise
                transfer the Software or access to the Software to any third
                party
              </li>
              <li>
                Remove, alter, or obscure any proprietary notices, labels, or
                marks on the Software
              </li>
              <li>
                Use the Software to develop a competing product or service
              </li>
              <li>
                Circumvent or disable any security, authentication, or access
                control features of the Software
              </li>
              <li>
                Use the Software in any manner that violates HIPAA, applicable
                privacy laws, or any other applicable law or regulation
              </li>
              <li>
                Access the Software through automated means (bots, scripts,
                crawlers) without our express written consent
              </li>
              <li>
                Transmit any malware, viruses, or other harmful code through the
                Software
              </li>
            </ul>
          </section>

          {/* 4. Subscription & Fees */}
          <section id="subscription" className="legal-section">
            <h2>4. Subscription & Fees</h2>

            <h3>4.1 Subscription Plans</h3>
            <p>
              Access to the Software requires an active paid subscription.
              Subscription plans, pricing, and included features are detailed on
              our{' '}
              <Link to="/pricing">Pricing page</Link> and in the service
              agreement executed with your organization.
            </p>

            <h3>4.2 Payment Terms</h3>
            <p>
              Subscription fees are billed in advance on a monthly or annual
              basis as agreed in your service agreement. All fees are
              non-refundable except as expressly stated in the service agreement
              or as required by law.
            </p>

            <h3>4.3 Usage-Based Charges</h3>
            <p>
              Certain features may incur usage-based charges (e.g., AI
              processing, transcription minutes, clinical coding requests).
              Usage is tracked and reported through the platform&apos;s AI Cost
              Dashboard. Usage exceeding your plan&apos;s included allowances
              will be billed at the rates specified in your service agreement.
            </p>

            <h3>4.4 Price Changes</h3>
            <p>
              We may adjust subscription pricing with at least sixty (60)
              days&apos; written notice before your next renewal period. You may
              terminate your subscription before the new pricing takes effect.
            </p>
          </section>

          {/* 5. Data Ownership */}
          <section id="data-ownership" className="legal-section">
            <h2>5. Data Ownership</h2>

            <h3>5.1 Your Data</h3>
            <div className="legal-info-box">
              <p>
                <strong>You own your data.</strong> All clinical data, patient
                records, transcriptions, clinical notes, and any other content
                created by you or processed on your behalf through the Software
                (&quot;Customer Data&quot;) remains the exclusive property of
                your healthcare organization.
              </p>
            </div>

            <h3>5.2 License to Process</h3>
            <p>
              You grant us a limited license to access, process, store, and
              transmit your Customer Data solely for the purpose of providing
              the Software&apos;s services to you. This license terminates upon
              expiration or termination of your subscription, subject to
              applicable data retention requirements.
            </p>

            <h3>5.3 No Use for AI Training</h3>
            <p>
              We will not use your Customer Data, including any PHI, to train,
              improve, or develop AI or machine learning models. Your data is
              processed solely to deliver the clinical documentation services
              described in this Agreement.
            </p>

            <h3>5.4 Data Export</h3>
            <p>
              You may export your Customer Data at any time during your active
              subscription. Upon termination, you will have ninety (90) days to
              request a data export before Customer Data is permanently deleted
              in accordance with our data retention policies.
            </p>
          </section>

          {/* 6. Confidentiality & HIPAA */}
          <section id="confidentiality" className="legal-section">
            <h2>6. Confidentiality & HIPAA</h2>

            <h3>6.1 Confidential Information</h3>
            <p>
              Each party agrees to treat as confidential all non-public
              information received from the other party that is designated as
              confidential or that a reasonable person would understand to be
              confidential given the nature of the information. Confidential
              Information shall not be disclosed to third parties except as
              necessary to perform obligations under this Agreement.
            </p>

            <h3>6.2 HIPAA Compliance</h3>
            <p>
              The Software is designed, operated, and maintained in compliance
              with the Health Insurance Portability and Accountability Act of
              1996 (HIPAA), as amended, including the HITECH Act and the Omnibus
              Rule. We act as a Business Associate when processing PHI on behalf
              of Covered Entities.
            </p>

            <h3>6.3 Business Associate Agreement</h3>
            <p>
              A Business Associate Agreement (BAA) is required before any PHI is
              processed through the Software. The BAA establishes the permitted
              uses and disclosures of PHI, security requirements, and breach
              notification obligations. The BAA is executed separately between
              your organization and Harris CareTracker.
            </p>

            <h3>6.4 Security Measures</h3>
            <p>
              We implement administrative, physical, and technical safeguards as
              required by HIPAA, including encryption at rest and in transit,
              role-based access controls, audit logging, and regular security
              assessments. For a detailed description of our security practices,
              please refer to our{' '}
              <Link to="/privacy">Privacy Policy</Link>.
            </p>
          </section>

          {/* 7. Support & Maintenance */}
          <section id="support" className="legal-section">
            <h2>7. Support & Maintenance</h2>

            <h3>7.1 Technical Support</h3>
            <p>
              Your subscription includes access to technical support during
              standard business hours. Support is available via email, phone, and
              the in-platform support system. Response times and support levels
              are defined in your service agreement.
            </p>

            <h3>7.2 Updates</h3>
            <p>
              We regularly release updates to the Software that may include bug
              fixes, security patches, performance improvements, and new
              features. Updates are deployed automatically for the web
              application and are made available through app stores for mobile
              applications.
            </p>

            <h3>7.3 Maintenance Windows</h3>
            <p>
              Scheduled maintenance is performed during off-peak hours with
              advance notice. Emergency maintenance to address security
              vulnerabilities or critical issues may occur without advance notice.
            </p>
          </section>

          {/* 8. Warranty Disclaimers */}
          <section id="warranty" className="legal-section">
            <h2>8. Warranty Disclaimers</h2>
            <p>
              THE SOFTWARE IS PROVIDED &quot;AS IS&quot; AND &quot;AS
              AVAILABLE&quot; WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR
              IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF
              MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
              NON-INFRINGEMENT, OR ACCURACY.
            </p>
            <p>
              WITHOUT LIMITING THE FOREGOING, WE DO NOT WARRANT THAT:
            </p>
            <ul>
              <li>
                The Software will meet all of your specific requirements
              </li>
              <li>
                The Software will be uninterrupted, timely, secure, or
                error-free
              </li>
              <li>
                The results obtained from the use of the Software, including
                AI-generated transcriptions, clinical summaries, and diagnostic
                codes, will be accurate or reliable
              </li>
              <li>
                Any errors in the Software will be corrected within a specific
                timeframe
              </li>
            </ul>
            <div className="legal-info-box">
              <p>
                <strong>Clinical Disclaimer:</strong> The Software is a clinical
                documentation tool, not a medical device. AI-generated content is
                intended to assist, not replace, the clinical judgment of
                qualified healthcare professionals. All AI output requires
                professional review before clinical use.
              </p>
            </div>
          </section>

          {/* 9. Limitation of Liability */}
          <section id="liability" className="legal-section">
            <h2>9. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT
              SHALL HARRIS CARETRACKER, ITS PARENT COMPANY (HARRIS COMPUTER
              SYSTEMS / CONSTELLATION SOFTWARE), AFFILIATES, OFFICERS,
              DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE FOR:
            </p>
            <ul>
              <li>
                Any indirect, incidental, special, consequential, or punitive
                damages
              </li>
              <li>
                Any loss of profits, revenue, data, goodwill, or other
                intangible losses
              </li>
              <li>
                Any damages resulting from clinical decisions made based on
                AI-generated content
              </li>
              <li>
                Any interruption of service or loss of access to the Software
              </li>
              <li>
                Any unauthorized access to or alteration of your data
              </li>
            </ul>
            <p>
              OUR TOTAL LIABILITY UNDER THIS AGREEMENT SHALL NOT EXCEED THE
              TOTAL FEES PAID BY YOUR ORGANIZATION IN THE TWELVE (12) MONTHS
              PRECEDING THE EVENT GIVING RISE TO THE CLAIM.
            </p>
          </section>

          {/* 10. Termination */}
          <section id="termination" className="legal-section">
            <h2>10. Termination</h2>

            <h3>10.1 Termination by You</h3>
            <p>
              You may terminate this Agreement by canceling your subscription in
              accordance with the terms of your service agreement. Individual
              users may deactivate their accounts through their organization
              administrator.
            </p>

            <h3>10.2 Termination by Us</h3>
            <p>
              We may terminate this Agreement immediately upon written notice if
              you materially breach any provision of this EULA, including but not
              limited to unauthorized use, security violations, or HIPAA
              non-compliance. For non-material breaches, we will provide thirty
              (30) days&apos; notice and an opportunity to cure.
            </p>

            <h3>10.3 Effect of Termination</h3>
            <p>Upon termination of this Agreement:</p>
            <ul>
              <li>
                Your license to use the Software immediately terminates
              </li>
              <li>
                You must cease all use of the Software and delete any installed
                mobile applications
              </li>
              <li>
                Your organization has ninety (90) days to request export of
                Customer Data
              </li>
              <li>
                After the export period, Customer Data will be deleted per our
                retention policy, except as required by law
              </li>
              <li>
                Provisions that by their nature should survive termination
                (including confidentiality, limitation of liability,
                indemnification, and governing law) shall survive
              </li>
            </ul>
          </section>

          {/* 11. Updates & Modifications */}
          <section id="updates" className="legal-section">
            <h2>11. Updates & Modifications to This EULA</h2>
            <p>
              We reserve the right to modify this EULA at any time. We will
              notify you of material changes by email and through the Software at
              least thirty (30) days before the changes take effect. Your
              continued use of the Software after the effective date of any
              changes constitutes your acceptance of the modified EULA.
            </p>
            <p>
              If you do not agree with the modified terms, you must discontinue
              use of the Software before the changes take effect and may
              terminate your subscription in accordance with Section 10.
            </p>
          </section>

          {/* 12. Governing Law */}
          <section id="governing-law" className="legal-section">
            <h2>12. Governing Law</h2>
            <p>
              This Agreement shall be governed by and construed in accordance
              with the laws of the State of New York, without regard to conflict
              of laws principles. Any disputes arising under this Agreement
              shall be subject to the exclusive jurisdiction of the state and
              federal courts located in Niagara County, New York.
            </p>
          </section>

          {/* 13. General Provisions */}
          <section id="general" className="legal-section">
            <h2>13. General Provisions</h2>

            <h3>13.1 Entire Agreement</h3>
            <p>
              This EULA, together with the Terms of Service, Privacy Policy, and
              any applicable Business Associate Agreement and service agreement,
              constitutes the entire agreement between you and Harris CareTracker
              regarding the Software.
            </p>

            <h3>13.2 Severability</h3>
            <p>
              If any provision of this EULA is found to be unenforceable, the
              remaining provisions shall continue in full force and effect.
            </p>

            <h3>13.3 Waiver</h3>
            <p>
              The failure of either party to enforce any right or provision of
              this EULA shall not constitute a waiver of such right or provision.
            </p>

            <h3>13.4 Assignment</h3>
            <p>
              You may not assign or transfer this Agreement without our prior
              written consent. We may assign this Agreement in connection with a
              merger, acquisition, or sale of all or substantially all of our
              assets.
            </p>

            <h3>13.5 Force Majeure</h3>
            <p>
              Neither party shall be liable for any failure to perform due to
              causes beyond its reasonable control, including natural disasters,
              war, terrorism, pandemic, labor disputes, government actions,
              internet outages, or failures of third-party services.
            </p>
          </section>

          {/* 14. Contact */}
          <section id="contact" className="legal-section">
            <h2>14. Contact Information</h2>
            <p>
              For questions about this EULA or licensing matters, please contact
              us:
            </p>
            <div className="legal-contact-box">
              <h3>Harris CareTracker — Amplify Licensing</h3>
              <p>
                <strong>Address:</strong> 2429 Military Rd, Suite 300, Niagara
                Falls, NY 14304
              </p>
              <p>
                <strong>Phone:</strong>{' '}
                <a href="tel:+18555284357">(855) 528-4357</a>
              </p>
              <p>
                <strong>Email:</strong>{' '}
                <a href="mailto:sales@harriscaretracker.com">
                  sales@harriscaretracker.com
                </a>
              </p>
              <p>
                <strong>Website:</strong>{' '}
                <a
                  href="https://harriscaretracker.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  harriscaretracker.com
                </a>
              </p>
            </div>
          </section>
        </div>
      </div>

      <MarketingFooter />
    </div>
  );
};

export default EULA;
